import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp, ShoppingCart, CreditCard, Package, Users, Shield, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import SEOHead, { generateFAQSchema } from '@/components/SEOHead';

const faqs = [
  {
    category: "Buying",
    icon: ShoppingCart,
    color: "#00F0FF",
    questions: [
      {
        q: "How do I purchase a product?",
        a: "Browse our marketplace, find a product you like, and click 'Add to Cart' or 'Buy Now'. Complete the checkout process by entering your payment information. Once payment is confirmed, you'll receive instant access to your purchased products."
      },
      {
        q: "How do I access my purchased products?",
        a: "After purchase, you'll receive an email with download links. You can also access all your purchases anytime by going to 'My Purchases' in your account. Download links are valid for 7 days with up to 10 downloads per link."
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit cards (Visa, MasterCard, American Express, Discover) through our secure Stripe payment processing. All transactions are encrypted and secure."
      },
      {
        q: "Can I get a refund?",
        a: "Refund policies are set by individual sellers. Please review the seller's refund policy before making a purchase. If you need a refund, contact the seller directly. If you cannot reach a resolution, our support team can assist with mediation."
      }
    ]
  },
  {
    category: "Selling",
    icon: CreditCard,
    color: "#00F0FF",
    questions: [
      {
        q: "How do I become a seller?",
        a: "Sign up for an account and select 'Seller' as your account type. You'll need to connect your Stripe account to receive payments. Once verified, you can start listing your products immediately."
      },
      {
        q: "What fees does theailedger.ai charge?",
        a: "We charge a 5% platform fee on all successful transactions. This covers payment processing, hosting, and platform maintenance. If you have affiliates, their commission is paid from your share."
      },
      {
        q: "How do I receive payments?",
        a: "Payments are processed through Stripe Connect. Once a sale is complete, funds (minus platform fees) are transferred to your connected Stripe account. Payout timing depends on your Stripe account settings."
      },
      {
        q: "How do I upload my product files?",
        a: "Go to your Dashboard → Products tab → Click 'Files' on any product. You can upload ZIP, PDF, MP4, and other file formats up to 50MB. These files are automatically delivered to buyers after purchase."
      }
    ]
  },
  {
    category: "Products",
    icon: Package,
    color: "#00F0FF",
    questions: [
      {
        q: "What types of products can be sold?",
        a: "theailedger.ai specializes in AI products including: AI tools and software, prompts and templates, training courses, datasets, APIs, plugins, and digital assets. All products must be digital and legally owned by the seller."
      },
      {
        q: "How do I know if a product is legitimate?",
        a: "We verify all sellers before they can list products. Look for the 'Verified Seller' badge. Read reviews from other buyers and check the seller's profile. If something seems suspicious, report it to our team."
      },
      {
        q: "Can I preview products before buying?",
        a: "Many sellers offer video demos, screenshots, and detailed descriptions. Some may offer free trials or samples. Check the product page for available previews."
      }
    ]
  },
  {
    category: "Affiliate Program",
    icon: Users,
    color: "#FF6B6B",
    questions: [
      {
        q: "How does the affiliate program work?",
        a: "Sellers can enable affiliate programs for their products with custom commission rates (typically 20-50%). Affiliates get unique referral links. When someone purchases through your link, you earn the commission."
      },
      {
        q: "How do I become an affiliate?",
        a: "Visit any product with an affiliate program enabled (look for the affiliate badge). Click 'Become an Affiliate' and you'll receive your unique referral link. Share it anywhere to start earning."
      },
      {
        q: "When do I get paid as an affiliate?",
        a: "Affiliate commissions are tracked and paid out according to each seller's program terms. Most payments are processed alongside the seller's regular payout schedule."
      }
    ]
  },
  {
    category: "Account & Security",
    icon: Shield,
    color: "#00FF94",
    questions: [
      {
        q: "How do I reset my password?",
        a: "Click 'Sign In' → 'Forgot Password?' and enter your email. You'll receive a password reset link valid for 1 hour. Click the link and create a new password."
      },
      {
        q: "Is my payment information secure?",
        a: "Yes! We use Stripe for payment processing, which is PCI DSS Level 1 compliant (the highest level of certification). We never store your full credit card details on our servers."
      },
      {
        q: "Can I delete my account?",
        a: "Yes, you can request account deletion by contacting our support team. Note that sellers with pending transactions or payouts must resolve those before deletion."
      }
    ]
  },
  {
    category: "Support",
    icon: MessageCircle,
    color: "#FFA500",
    questions: [
      {
        q: "How do I contact support?",
        a: "You can reach us at support@theailedger.ai. For faster response, use our live chat feature (coming soon). We typically respond within 24 hours on business days."
      },
      {
        q: "I have a dispute with a seller/buyer. What should I do?",
        a: "First, try to resolve the issue directly with the other party. If that doesn't work, contact our support team with details of the issue, and we'll assist with mediation."
      },
      {
        q: "How do I report a fraudulent product or seller?",
        a: "Use the 'Report' button on the product page or contact support@theailedger.ai with details. We take fraud seriously and investigate all reports promptly."
      }
    ]
  }
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-white/5 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left hover:text-[#00F0FF] transition-colors"
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0 text-[#00F0FF]" />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0 text-neutral-500" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 text-neutral-400 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

function FAQs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    // Scroll to top on page load
    // scroll handled by <ScrollRestoration />
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEOHead
        title="FAQ - Frequently Asked Questions"
        description="Find answers to common questions about theailedger.ai AI marketplace. Learn about buying AI tools, selling AI products, affiliate programs, payments, security, and support."
        path="/faqs"
        keywords="theailedger FAQ, AI marketplace help, how to buy AI tools, how to sell AI products, AI marketplace affiliate program, AI marketplace payments, theailedger support"
        schema={generateFAQSchema(
          faqs.flatMap(cat => cat.questions.map(q => ({ question: q.q, answer: q.a })))
        )}
      />
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-[#00F0FF] hover:text-[#00C2CC]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Marketplace
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-8 h-8 text-[#00F0FF]" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Frequently Asked Questions</h1>
            <p className="text-neutral-400">Find answers to common questions about theailedger.ai</p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            <Button
              variant={activeCategory === null ? "default" : "outline"}
              onClick={() => setActiveCategory(null)}
              className={`rounded-full ${activeCategory === null ? 'bg-[#00F0FF] text-black' : 'border-white/10 hover:bg-white/5'}`}
            >
              All
            </Button>
            {faqs.map((cat) => (
              <Button
                key={cat.category}
                variant={activeCategory === cat.category ? "default" : "outline"}
                onClick={() => setActiveCategory(cat.category)}
                className={`rounded-full ${activeCategory === cat.category ? 'bg-[#00F0FF] text-black' : 'border-white/10 hover:bg-white/5'}`}
              >
                <cat.icon className="w-4 h-4 mr-2" style={{ color: activeCategory === cat.category ? 'black' : cat.color }} />
                {cat.category}
              </Button>
            ))}
          </div>

          {/* FAQ Categories */}
          <div className="space-y-8">
            {faqs
              .filter(cat => activeCategory === null || activeCategory === cat.category)
              .map((category) => (
                <div key={category.category} className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
                    <category.icon className="w-5 h-5" style={{ color: category.color }} />
                    <h2 className="text-xl font-bold">{category.category}</h2>
                  </div>
                  <div className="px-6">
                    {category.questions.map((faq, idx) => (
                      <FAQItem key={idx} question={faq.q} answer={faq.a} />
                    ))}
                  </div>
                </div>
              ))}
          </div>

          {/* Still need help */}
          <div className="mt-12 bg-gradient-to-r from-[#00F0FF]/10 to-white/10 border border-white/10 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Still have questions?</h3>
            <p className="text-neutral-400 mb-6">
              Can't find what you're looking for? Our support team is here to help!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full px-8"
              >
                Contact Support
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = 'mailto:support@theailedger.ai'}
                className="border-white/10 hover:bg-white/5 rounded-full px-8"
              >
                Email Us
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FAQs;
